import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useNavigate } from 'react-router-dom';
import Button from '@material-ui/core/Button';

import createPickMuiClasses from '../../util/material-ui';
import { hasPulseJWE } from '../../redux/authentication/utils';
import { useTracking } from '../../vNext/hooks/useTracking/useTracking';

const classesKeys = [
    'root',
    'text',
    'contained',
    'disabled',
    'outlined',
    'label',
];

const pickMuiClasses = createPickMuiClasses(classesKeys);

const ButtonComponent = ({
    href,
    to,
    onClick,
    variant,
    className,
    trackingPayload,
    children,
    dataIdentifier = undefined,
    text = '',
    disabled = false,
    icon = {},
    color = 'primary',
    theme = {},
    TouchRippleProps,
    target,
    testId,
    rel,
    dataset,
}) => {
    const navigate = useNavigate();
    const classNames = classnames({
        [className]: !!className,
        [theme.button]: !!theme.button,
    });
    const { trackEventWithParams } = useTracking();

    const handleClick = (event, ...clickRest) => {
        if (href && target !== '_blank') {
            window.location.assign(href);
        } else if (onClick) {
            onClick(event, ...clickRest);
        } else if (to) {
            navigate(to);
        }
    };

    const handleClickWithTracking = async (event, ...clickRest) => {
        const isPulseInitialized = hasPulseJWE();

        if (trackingPayload && isPulseInitialized) {
            event.preventDefault();

            try {
                trackEventWithParams(trackingPayload);
            } finally {
                handleClick(event, ...clickRest);
            }
        } else {
            handleClick(event, ...clickRest);
        }
    };

    return (
        <Button
            { ...{
                disabled,
                href,
                onClick: handleClickWithTracking,
                variant,
                color,
                TouchRippleProps,
                target,
                rel,
            } }
            classes={ pickMuiClasses(theme) }
            className={ classNames }
            data-identifier={ dataIdentifier }
            data-testid={ testId }
            data-disable-auto-tracker={
                !!(trackingPayload && trackingPayload.id)
            }
            disableElevation
            { ...dataset }
        >
            {icon && icon.position === 'left' && (
                <icon.component className={ theme.icon } />
            )}
            {children || <span className={ theme.text }>{text}</span>}
            {icon && icon.position === 'right' && (
                <icon.component className={ theme.icon } />
            )}
        </Button>
    );
};

const propTypes = {
    text: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    disabled: PropTypes.bool,
    href: PropTypes.string,
    onClick: PropTypes.func,
    to: PropTypes.string,
    icon: PropTypes.shape({
        position: PropTypes.oneOf(['left', 'right']),
        component: PropTypes.oneOfType([PropTypes.func, PropTypes.shape()]),
    }),
    className: PropTypes.string,
    trackEvent: PropTypes.func,
    trackingPayload: PropTypes.shape(),
    variant: PropTypes.oneOf(['text', 'contained', 'outlined']).isRequired,
    color: PropTypes.oneOf(['default', 'primary', 'secondary']),
    theme: PropTypes.shape({}),
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]),
    dataIdentifier: PropTypes.string,
    TouchRippleProps: PropTypes.shape({}),
    target: PropTypes.string,
    testId: PropTypes.string,
    rel: PropTypes.string,
    dataset: PropTypes.shape({}),
};

ButtonComponent.propTypes = propTypes;

export default ButtonComponent;
export { propTypes };
